/**
 * Custom Sticky Bar JavaScript
 * Handles sticky bar functionality with automatic USP sliders and quantity controls
 * Sticky bar visibility is controlled by the regular add to cart button visibility
 */

document.addEventListener('DOMContentLoaded', function () {
  'use strict';
  
  // Configuration constants
  const SLIDE_INTERVAL = 3000;
  const CSS_VISIBLE = {
    opacity: '1',
    visibility: 'visible', 
    transform: 'translateY(0)'
  };
  const CSS_HIDDEN = {
    opacity: '0',
    visibility: 'hidden',
    transform: 'translateY(100px)'
  };
  
  // Global state
  let intersectionObserver = null;
  let stickyBars = [];
  
  /**
   * Check if an element is visible in the viewport
   * @param {HTMLElement} element - The element to check
   * @returns {boolean} - True if element is in viewport
   */
  function isElementInViewport(element) {
    if (!element) return false;
    
    try {
      const rect = element.getBoundingClientRect();
      const windowHeight = window.innerHeight || document.documentElement.clientHeight;
      const windowWidth = window.innerWidth || document.documentElement.clientWidth;
      
      // Check if any part of the element is visible in the viewport
      return (
        rect.top < windowHeight &&
        rect.bottom > 0 &&
        rect.left < windowWidth &&
        rect.right > 0
      );
    } catch (error) {
      console.error('Error checking element visibility:', error);
      return false;
    }
  }
  
  /**
   * Update sticky bar visibility based on add to cart button visibility
   */
  function updateStickyBarVisibility() {
    const addToCartButton = document.querySelector('.product-form .product-form__cart-submit');
    
    if (!addToCartButton) {
      // If no add to cart button found, show sticky bars
      setStickyBarsVisibility(true);
      return;
    }
    
    const isButtonVisible = isElementInViewport(addToCartButton);
    
    // Show sticky bars when button is NOT visible, hide when it IS visible
    setStickyBarsVisibility(!isButtonVisible);
  }
  
  /**
   * Set visibility for all sticky bars
   * @param {boolean} visible - Whether to show or hide the sticky bars
   */
  function setStickyBarsVisibility(visible) {
    const cssProperties = visible ? CSS_VISIBLE : CSS_HIDDEN;
    
    stickyBars.forEach(function (stickyBar) {
      try {
        Object.entries(cssProperties).forEach(([property, value]) => {
          stickyBar.style.setProperty(property, value, 'important');
        });
      } catch (error) {
        console.error('Error updating sticky bar visibility:', error);
      }
    });
  }
  
  /**
   * Initialize sticky bars and visibility monitoring
   */
  function initializeStickyBars() {
    stickyBars = Array.from(document.querySelectorAll('.sticky-bar'));
    
    if (stickyBars.length === 0) {
      console.warn('No sticky bars found on the page');
      return;
    }
    
    // Initialize each sticky bar
    stickyBars.forEach(function (stickyBar) {
      try {
        // Initialize slider for this sticky bar
        initializeSlider(stickyBar);
      } catch (error) {
        console.error('Error initializing sticky bar:', error);
      }
    });
    
    // Set up viewport monitoring
    setupViewportMonitoring();
    
    // Initial visibility check
    updateStickyBarVisibility();
  }
  
  /**
   * Set up viewport monitoring for the add to cart button
   */
  function setupViewportMonitoring() {
    // Use Intersection Observer if available (more performant)
    if ('IntersectionObserver' in window) {
      const addToCartButton = document.querySelector('.product-form .product-form__cart-submit');
      
      if (addToCartButton) {
        intersectionObserver = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              // Show sticky bars when button is NOT intersecting (not visible)
              // Hide sticky bars when button IS intersecting (visible)
              setStickyBarsVisibility(!entry.isIntersecting);
            });
          },
          {
            // Trigger when any part of the button enters/leaves the viewport
            threshold: 0,
            // Add some root margin for better UX
            rootMargin: '0px 0px -10px 0px'
          }
        );
        
        intersectionObserver.observe(addToCartButton);
      }
    }
    
    // Fallback to scroll/resize event listeners
    if (!intersectionObserver) {
      let ticking = false;
      
      function handleViewportChange() {
        if (!ticking) {
          requestAnimationFrame(function () {
            updateStickyBarVisibility();
            ticking = false;
          });
          ticking = true;
        }
      }
      
      window.addEventListener('scroll', handleViewportChange, { passive: true });
      window.addEventListener('resize', handleViewportChange);
    }
  }
  
  /**
   * Initialize slider functionality for a sticky bar
   * @param {HTMLElement} stickyBar - The sticky bar element
   */
  function initializeSlider(stickyBar) {
    if (!stickyBar) return;
    
    try {
      const slider = stickyBar.querySelector('.sticky-bar__usps');
      const slides = stickyBar.querySelectorAll('.slide');
      
      if (!slider || slides.length === 0) {
        return;
      }
      
      let currentIndex = 0;
      let autoSlideInterval;
      
      /**
       * Update slider position
       */
      function updateSlider() {
        try {
          const offset = -currentIndex * 100;
          slider.style.transform = `translateX(${offset}%)`;
        } catch (error) {
          console.error('Error updating slider:', error);
        }
      }
      
      /**
       * Go to next slide
       */
      function goToNextSlide() {
        currentIndex = (currentIndex < slides.length - 1) ? currentIndex + 1 : 0;
        updateSlider();
      }
      
      /**
       * Go to previous slide
       */
      function goToPrevSlide() {
        currentIndex = (currentIndex > 0) ? currentIndex - 1 : slides.length - 1;
        updateSlider();
      }
      
      /**
       * Start automatic slide rotation
       */
      function startAutoSlide() {
        stopAutoSlide(); // Clear any existing interval
        autoSlideInterval = setInterval(goToNextSlide, SLIDE_INTERVAL);
      }
      
      /**
       * Stop automatic slide rotation
       */
      function stopAutoSlide() {
        if (autoSlideInterval) {
          clearInterval(autoSlideInterval);
          autoSlideInterval = null;
        }
      }
      
      // Pause auto-slide on hover
      stickyBar.addEventListener('mouseenter', stopAutoSlide);
      stickyBar.addEventListener('mouseleave', startAutoSlide);
      
      // Start the auto-slide
      startAutoSlide();
      
      // Expose slider controls for external use if needed
      stickyBar.sliderControls = {
        next: goToNextSlide,
        prev: goToPrevSlide,
        start: startAutoSlide,
        stop: stopAutoSlide,
        goTo: (index) => {
          if (index >= 0 && index < slides.length) {
            currentIndex = index;
            updateSlider();
          }
        }
      };
      
    } catch (error) {
      console.error('Error initializing slider:', error);
    }
  }
  
  // Initialize everything
  initializeStickyBars();

  // =============================
  // Sticky price sync with variant
  // =============================

  /**
   * Return all sticky bar price containers
   * @returns {HTMLElement[]}
   */
  function getStickyPriceContainers() {
    return Array.from(document.querySelectorAll('.sticky-bar .sticky-bar__price [data-price]'));
  }

  /**
   * Format money using theme's formatter when available
   * @param {number} cents
   * @returns {string}
   */
  function formatMoney(cents) {
    try {
      if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
        return theme.Currency.formatMoney(cents, theme.moneyFormat);
      }
    } catch (e) {}
    return (cents / 100).toFixed(2);
  }

  /**
   * Compute unit base unit string like "100 ml"
   * @param {object} variant
   * @returns {string}
   */
  function getBaseUnit(variant) {
    try {
      var m = variant && variant.unit_price_measurement;
      if (!m) return '';
      var parts = [];
      if (m.reference_value && m.reference_value !== 1) {
        parts.push(m.reference_value);
      }
      if (m.reference_unit) {
        parts.push(m.reference_unit);
      }
      return parts.join(' ');
    } catch (e) {
      return '';
    }
  }

  /**
   * Update classes on sticky price container based on variant state
   * @param {HTMLElement} priceContainer
   * @param {object} variant
   */
  function updateStickyPriceClasses(priceContainer, variant) {
    if (!priceContainer || !variant) return;
    var onSale = variant.compare_at_price > variant.price;
    var soldOut = !variant.available;
    var showUnit = !!variant.unit_price;

    priceContainer.classList.toggle('price--on-sale', onSale);
    priceContainer.classList.toggle('price--sold-out', soldOut);
    priceContainer.classList.toggle('price--unit-available', showUnit);
    priceContainer.classList.remove('price--unavailable');
    priceContainer.removeAttribute('aria-hidden');

    // Unit base unit text
    var unitBaseUnit = priceContainer.querySelector('[data-unit-price-base-unit]');
    if (unitBaseUnit) {
      unitBaseUnit.innerHTML = getBaseUnit(variant);
    }
  }

  /**
   * Update money values within sticky price container
   * @param {HTMLElement} priceContainer
   * @param {object} variant
   */
  function updateStickyPriceValues(priceContainer, variant) {
    if (!priceContainer || !variant) return;
    var onSale = variant.compare_at_price > variant.price;

    var regularPrices = priceContainer.querySelectorAll('[data-regular-price]');
    var salePrice = priceContainer.querySelector('[data-sale-price]');
    var unitPrice = priceContainer.querySelector('[data-unit-price]');

    if (onSale) {
      regularPrices.forEach(function (el) {
        el.innerHTML = formatMoney(variant.compare_at_price);
      });
      if (salePrice) {
        salePrice.innerHTML = formatMoney(variant.price);
      }
    } else {
      regularPrices.forEach(function (el) {
        el.innerHTML = formatMoney(variant.price);
      });
    }

    if (unitPrice && variant.unit_price) {
      unitPrice.innerHTML = formatMoney(variant.unit_price);
    }
  }

  /**
   * Update discount badge inside sticky price container
   * @param {HTMLElement} priceContainer
   * @param {object} variant
   */
  function updateStickyDiscountBadge(priceContainer, variant) {
    if (!priceContainer) return;
    var badge = priceContainer.querySelector('[data-percentage-badge]');
    if (!badge) return;
    if (variant && variant.compare_at_price > variant.price) {
      var savings = Math.round(((variant.compare_at_price - variant.price) * 100.0) / variant.compare_at_price);
      try {
        badge.innerHTML = badge.innerHTML.replace(/\d+/, String(savings));
      } catch (e) {}
      badge.classList.remove('hidden');
    } else {
      badge.classList.add('hidden');
    }
  }

  // Listen for product variant lifecycle events and mirror updates to sticky bar
  document.addEventListener('variantChange', function (evt) {
    try {
      var variant = evt && evt.detail && evt.detail.variant;
      if (!variant) return;
      
      // Update sticky bar price classes
      getStickyPriceContainers().forEach(function (container) {
        updateStickyPriceClasses(container, variant);
      });
      
      // Update sticky bar variant ID for add to cart
      var stickyVariantInput = document.querySelector('[data-sticky-variant-id]');
      if (stickyVariantInput && variant.id) {
        stickyVariantInput.value = variant.id;
      }
    } catch (error) {
      console.error('Error updating sticky price on variantChange:', error);
    }
  });

  document.addEventListener('variantPriceChange', function (evt) {
    try {
      var variant = evt && evt.detail && evt.detail.variant;
      if (!variant) return;
      getStickyPriceContainers().forEach(function (container) {
        updateStickyPriceValues(container, variant);
        updateStickyDiscountBadge(container, variant);
      });
    } catch (error) {
      console.error('Error updating sticky price on variantPriceChange:', error);
    }
  });
});

/**
 * Adjust quantity for sticky bar
 * @param {number} change - The change in quantity (positive or negative)
 * @param {HTMLElement} stickyBarElement - Optional specific sticky bar element
 */
function adjustQuantity(change, stickyBarElement) {
  'use strict';
  
  if (typeof change !== 'number') {
    console.error('adjustQuantity: change must be a number');
    return;
  }
  
  try {
    let quantityInput;
    
    if (stickyBarElement && stickyBarElement instanceof HTMLElement) {
      quantityInput = stickyBarElement.querySelector('.sticky-quantity');
    } else {
      quantityInput = document.querySelector('.sticky-quantity');
    }
    
    if (!quantityInput) {
      console.warn('No quantity input found');
      return;
    }
    
    const currentValue = parseInt(quantityInput.value, 10) || 1;
    const newValue = Math.max(currentValue + change, 1);
    
    quantityInput.value = newValue;
    
    // Dispatch change event for any listeners
    const event = new Event('change', { bubbles: true });
    quantityInput.dispatchEvent(event);
    
  } catch (error) {
    console.error('Error adjusting quantity:', error);
  }
}

// Export functions for external use if needed
if (typeof window !== 'undefined') {
  window.StickyBarUtils = {
    adjustQuantity: adjustQuantity
  };
}